/** Labels, status e mensagens compartilhadas das telas de Campanhas. */

export const CHANNEL_LABELS = {
  whatsapp: "WhatsApp",
  email: "E-mail",
  sms: "SMS",
  voice: "Telefone Xpro",
  instagram: "Instagram",
  messenger: "Messenger",
};

/** Canais oficiais do produto de campanhas (escopo 100%). */
export const CAMPAIGN_CHANNEL_KEYS = [
  "whatsapp",
  "email",
  "sms",
  "voice",
  "instagram",
  "messenger",
];

export const STATUS_LABELS = {
  draft: "Rascunho",
  scheduled: "Agendada",
  running: "Em envio",
  paused: "Pausada",
  completed: "Concluída",
  cancelled: "Cancelada",
  active: "Ativa",
};

export const STATUS_COLORS = {
  draft: "default",
  scheduled: "info",
  running: "success",
  paused: "warning",
  completed: "primary",
  cancelled: "error",
  active: "success",
};

export function labelChannel(key) {
  const raw = String(key || "").trim();
  if (!raw) return null;
  return CHANNEL_LABELS[raw] || raw;
}

/** Rótulo de saída do canal: tipo · identity (e-mail/número). */
export function formatChannelIdentity(channelKey, identity) {
  const typeLabel = labelChannel(channelKey) || channelKey || "Canal";
  const id = String(identity || "").trim();
  if (!id) return typeLabel;
  return `${typeLabel} · ${id}`;
}

/** Texto curto do canal de saída (nome da origem + identity). */
export function formatOutboundChannelLabel({
  channelKey,
  originName,
  originIdentity,
  channels,
} = {}) {
  const key =
    channelKey ||
    (Array.isArray(channels) && channels.length ? channels[0] : null) ||
    null;
  const name = String(originName || "").trim();
  const identity = String(originIdentity || "").trim();
  if (name && identity) return `${name} · ${identity}`;
  if (name && key) return `${name} · ${labelChannel(key) || key}`;
  if (name) return name;
  if (identity) return formatChannelIdentity(key, identity);
  return labelChannel(key) || key || "—";
}

/** Canais de disparo frio que exigem remetente/From configurado. */
export function channelRequiresSenderIdentity(channelKey) {
  const key = String(channelKey || "").toLowerCase();
  return key === "email" || key === "sms" || key === "voice";
}

export function labelStatus(status) {
  const raw = String(status || "").trim().toLowerCase();
  if (!raw) return "—";
  return STATUS_LABELS[raw] || raw;
}

export const LEAD_STATUS_LABELS = {
  0: "Pendente",
  1: "Em atendimento",
  2: "Concluída",
  3: "Contato futuro",
  4: "Perdida",
  5: "Cancelada",
};

export function labelLeadStatus(status) {
  if (status == null || status === "") return "—";
  const n = Number(status);
  if (Number.isInteger(n) && LEAD_STATUS_LABELS[n] != null) return LEAD_STATUS_LABELS[n];
  return String(status);
}

export function statusColor(status) {
  const raw = String(status || "").trim().toLowerCase();
  return STATUS_COLORS[raw] || "default";
}

export function labelObjective(value) {
  const map = {
    gerar_leads: "Gerar leads",
    vender_mais: "Vender mais",
    recuperar_clientes: "Recuperar clientes",
    broadcast: "Disparo",
  };
  return map[value] || value || null;
}

/** Motivo amigável de falha de destinatário (voz etc.). */
export function labelRecipientErrorReason(reason) {
  const raw = String(reason || "").trim().toLowerCase();
  if (!raw) return null;
  if (raw === "undelivered_no_answer" || raw === "undelivered") {
    return "Não atendida (não entregue)";
  }
  if (raw === "busy") return "Ocupado";
  if (raw === "no-answer" || raw === "no_answer") return "Não atendeu";
  return String(reason);
}

export function formatCount(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("pt-BR");
}

/** Taxa 0–1 → "42,5%"; valores inválidos → "—". */
export function formatRate(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "—";
  return `${(n * 100).toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

export function formatMoney(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

/**
 * Agenda de disparo: exibe o horário civil gravado, sem deslocar via `new Date()`
 * (ISO com Z virava 3h a menos/mais na UI e parecia "preso" no fuso errado).
 */
export function formatScheduleAt(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const civil = raw
    .replace("T", " ")
    .replace(/\.\d{1,3}Z?$/i, "")
    .replace(/Z$/i, "")
    .replace(/\s*[+-]\d{2}:?\d{2}$/, "");
  const m = civil.match(/^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}:${m[6] || "00"}`;
  }
  // Fallback: se for ISO absoluto, mostra no fuso de Brasília de forma explícita
  if (/Z$/i.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
    }
  }
  return raw;
}

export function estimateSmsSegments(text) {
  const value = String(text || "");
  if (!value) return { chars: 0, segments: 0 };
  // GSM-7 básico + extensões comuns; evita control chars no regex (eslint no-control-regex).
  const GSM_BASIC =
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./:;<=>?¡ÄÖÑÜ§¿äöñüà";
  const GSM_EXTENDED = "^{}\\[~]|€";
  const isGsm = [...value].every(
    (ch) => GSM_BASIC.includes(ch) || GSM_EXTENDED.includes(ch) || (ch >= "0" && ch <= "9") || (ch >= "A" && ch <= "Z") || (ch >= "a" && ch <= "z")
  );
  const limit = isGsm ? 160 : 70;
  const concatLimit = isGsm ? 153 : 67;
  const chars = value.length;
  if (chars <= limit) return { chars, segments: 1, encoding: isGsm ? "GSM" : "Unicode" };
  return {
    chars,
    segments: Math.ceil(chars / concatLimit),
    encoding: isGsm ? "GSM" : "Unicode",
  };
}

export function toFriendlyMessage(err) {
  const raw =
    (err && typeof err === "object" && (err.message || err.errorMessage || err.error)) ||
    (typeof err === "string" ? err : "") ||
    "";
  const status = err?.status ?? err?.response?.status ?? err?.response?.data?.status ?? null;
  if (status >= 500) return "Erro temporário no servidor. Tente novamente em instantes.";
  if (status === 401 || status === 403) return "Você não tem permissão para acessar este recurso.";
  if (status === 404) return "Recurso não encontrado.";
  return raw || "Não foi possível concluir a operação. Tente novamente.";
}

/** UFs brasileiras para filtros de audiência (select). */
export const BR_UF_OPTIONS = [
  { code: "AC", name: "Acre" },
  { code: "AL", name: "Alagoas" },
  { code: "AP", name: "Amapá" },
  { code: "AM", name: "Amazonas" },
  { code: "BA", name: "Bahia" },
  { code: "CE", name: "Ceará" },
  { code: "DF", name: "Distrito Federal" },
  { code: "ES", name: "Espírito Santo" },
  { code: "GO", name: "Goiás" },
  { code: "MA", name: "Maranhão" },
  { code: "MT", name: "Mato Grosso" },
  { code: "MS", name: "Mato Grosso do Sul" },
  { code: "MG", name: "Minas Gerais" },
  { code: "PA", name: "Pará" },
  { code: "PB", name: "Paraíba" },
  { code: "PR", name: "Paraná" },
  { code: "PE", name: "Pernambuco" },
  { code: "PI", name: "Piauí" },
  { code: "RJ", name: "Rio de Janeiro" },
  { code: "RN", name: "Rio Grande do Norte" },
  { code: "RS", name: "Rio Grande do Sul" },
  { code: "RO", name: "Rondônia" },
  { code: "RR", name: "Roraima" },
  { code: "SC", name: "Santa Catarina" },
  { code: "SP", name: "São Paulo" },
  { code: "SE", name: "Sergipe" },
  { code: "TO", name: "Tocantins" },
];

